import { useCallback, useEffect, useRef, useState } from 'react';

type AuthState = {
  token: string | null;
  serverState: 'unknown' | 'ok' | 'error';
  status: string;
  authFetch: (url: string, options?: RequestInit, retry?: boolean) => Promise<Response>;
  refreshToken: () => Promise<string | null>;
};

export function useAuth(apiBase: string, apiKey: string, apiSecret: string): AuthState {
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState('Ready');
  const [serverState, setServerState] = useState<'unknown' | 'ok' | 'error'>('unknown');
  const tokenRef = useRef<string | null>(null);

  const refreshToken = useCallback(async () => {
    if (!apiBase || !apiKey || !apiSecret) {
      setStatus('API ortam değişkenleri eksik');
      setServerState('error');
      return null;
    }
    try {
      setStatus('Token alınıyor…');
      const res = await fetch(`${apiBase}/get-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: apiKey, secret: apiSecret }),
      });
      const data = await res.json();
      if (data?.status && data?.result?.token) {
        setToken(data.result.token);
        tokenRef.current = data.result.token;
        setStatus('Token hazır');
        setServerState('ok');
        return data.result.token;
      }
      setStatus('Token alınamadı');
      setServerState('error');
      return null;
    } catch (error) {
      console.error('Token error', error);
      setStatus('Token hatası');
      setServerState('error');
      return null;
    }
  }, [apiBase, apiKey, apiSecret]);

  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}, retry = true): Promise<Response> => {
      const headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
        ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}),
      };
      const res = await fetch(url, { ...options, headers });
      if (res.status === 401 && retry) {
        const newToken = await refreshToken();
        if (newToken) {
          return authFetch(url, options, false);
        }
      }
      return res;
    },
    [refreshToken]
  );

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (typeof fetch === 'undefined') {
      setStatus('fetch kullanılamıyor (test ortamı)');
      setServerState('error');
      return;
    }
    void refreshToken();
  }, [refreshToken, apiBase, apiKey, apiSecret]);

  return { token, serverState, status, authFetch, refreshToken };
}
